<script setup lang="ts">
import { computed, defineAsyncComponent, inject, onBeforeUnmount, onMounted, ref } from "vue";
import CodeMirror from "vue-codemirror6";
import { EditorState, Prec, type Extension } from "@codemirror/state";
import { EditorView, keymap, tooltips, type ViewUpdate } from "@codemirror/view";
import {
  acceptCompletion,
  autocompletion,
  closeBrackets,
  closeBracketsKeymap,
  completeFromList,
  completionKeymap,
  ifNotIn,
  type CompletionSource,
} from "@codemirror/autocomplete";
import { bracketMatching } from "@codemirror/language";
import { json } from "@codemirror/lang-json";
import { xml } from "@codemirror/lang-xml";
import { javascript, localCompletionSource, snippets as jsSnippets } from "@codemirror/lang-javascript";
import type { Diagnostic } from "@codemirror/lint";
import type { BbelFieldLang, BbelFieldShape, ScopeDescriptor } from "@/types/bbel-types";
import { useBbelValidation } from "@/queries/expressions";
import { bbelBaseLangHighlight, bbelSlotHighlight } from "./bbel-language";
import { bbelCompletionSource, bbelFunctionHover, chainAncestorPaths, insideSlot } from "./bbel-autocomplete";
import { bbelSignatureHelp } from "./bbel-signature-help";
import { advisoryMemberDiagnostics, extractSlots, structuralDiagnostics, toCmDiagnostic } from "./bbel-lint";
import { planInsert } from "./bbel-palette";
import {
  BBEL_PICKER_HOST_KEY,
  type BbelInsertTarget,
  type BbelPickerHost,
} from "@/views/builder/composables/bbel-context";

/**
 * The universal BBEL expression field (#5683, ADR-017): CodeMirror 6 via vue-codemirror6, with the
 * BBEL grammar overlay, scope/function autocomplete, advisory squiggles, and a corner expand icon
 * opening the fullscreen editor (BbelEditorModal). Context-free — the caller passes the
 * {@link ScopeDescriptor} explicitly (no ambient inject), so the same component serves the
 * workflow builder and the connection-template authoring screen (#5759).
 */
const props = withDefaults(
  defineProps<{
    modelValue: string;
    scope: ScopeDescriptor;
    fieldShape?: BbelFieldShape;
    /** Content language of the literal text around the slots (JSON/XML get real syntax highlighting). */
    lang?: BbelFieldLang;
    placeholder?: string;
    disabled?: boolean;
    /** One-line field (e.g. the HTTP URL) — rejects edits that would introduce a second line.
     *  Long values soft-wrap up to four visual lines, then scroll. */
    singleLine?: boolean;
    /** Corner expand icon opening the fullscreen editor — off only for the editor inside that modal. */
    expandable?: boolean;
  }>(),
  { fieldShape: "text", lang: "text", placeholder: "", disabled: false, singleLine: false, expandable: true },
);

const emit = defineEmits<{
  "update:modelValue": [value: string];
  /** The full lint result (errors and warnings) — feeds the fullscreen modal's problems panel. */
  diagnostics: [diagnostics: Diagnostic[]];
}>();

// Async so BbelField ↔ BbelEditorModal (which embeds a BbelField) isn't a static import cycle;
// also keeps the modal out of the initial chunk.
const BbelEditorModal = defineAsyncComponent(() => import("./BbelEditorModal.vue"));
const expanded = ref(false);

const value = computed({
  get: () => props.modelValue ?? "",
  set: (v: string) => emit("update:modelValue", v),
});

// The base language is the field's content lang — JSON/XML bodies get real syntax highlighting,
// bracket matching, and folding; plain fields get none. BBEL slot colouring is a decoration overlay
// (bbelSlotHighlight, in `extensions`) that sits above whichever base is active, so `{{ }}` keeps
// its colour even mid-JSON-string. Reactive: vue-codemirror6 reconfigures on lang change.
const baseLang = computed(() =>
  props.lang === "json" ? json()
  : props.lang === "xml" ? xml()
  : props.lang === "javascript" ? javascript()
  : undefined,
);

// Native language completion (JS keywords / snippets / in-scope locals) fires only OUTSIDE a `{{ }}`
// slot — inside a slot BBEL owns the popup. JSON/XML/text have no schema-free native source, so only
// BBEL runs there. The JS sources mirror what `javascript()` registers via language data; `override`
// on the autocompletion extension bypasses that facet, so we replay them here through the same gate.
const outsideSlot = (src: CompletionSource): CompletionSource => (ctx) =>
  insideSlot(ctx.state.sliceDoc(0, ctx.pos)) ? null : src(ctx);

const JS_KEYWORDS =
  "break case const continue default delete export extends false finally in instanceof let new return static super switch this throw true typeof var yield".split(
    " ",
  );
const jsNativeSources: CompletionSource[] = [
  localCompletionSource,
  ifNotIn(
    ["LineComment", "BlockComment", "String", "TemplateString"],
    completeFromList([...jsSnippets, ...JS_KEYWORDS.map((label) => ({ label, type: "keyword" }))]),
  ),
].map(outsideSlot);

const nativeSources = computed<CompletionSource[]>(() =>
  props.lang === "javascript" ? jsNativeSources : [],
);

// Linting is two-layered (#5683): instant structural squiggles (unclosed / empty slot) computed
// synchronously, plus authoritative semantic squiggles (unknown function / member, banned grammar,
// argument counts) from the server validator. CodeMirror owns the debounce (linterConfig.delay); the
// server call is cached per (scope, slots). Semantic checks are skipped with no scope (read-only
// replay). The diagnostics also feed the persistent error line under the field, so the current
// problem reads without hovering the squiggle.
const { validate } = useBbelValidation();
const lintMessages = ref<string[]>([]);

async function lintSource(view: EditorView): Promise<Diagnostic[]> {
  const text = view.state.doc.toString();
  const diagnostics = structuralDiagnostics(text).map(toCmDiagnostic);
  // Advisory member warnings against the known shape (sampled output members, projections) —
  // amber squiggles, never errors; closed levels stay the server's. The caret (only while the
  // field has focus) scopes the typing-in-progress exemption to the segment being typed —
  // moving the caret away or leaving the field re-lints (linterConfig.needsRefresh) so a
  // committed wrong prefix warns.
  const caret = view.hasFocus ? view.state.selection.main.head : undefined;
  diagnostics.push(...advisoryMemberDiagnostics(text, props.scope.roots, caret).map(toCmDiagnostic));

  const slots = extractSlots(text);
  if (slots.length > 0 && props.scope.roots.length > 0) {
    try {
      const { results } = await validate(props.scope.roots, slots.map((s) => s.text));
      results.forEach((result, i) => {
        for (const message of result.messages)
          diagnostics.push({ from: slots[i].from, to: slots[i].to, severity: "error", message });
      });
    } catch {
      // Validation endpoint unreachable — keep the instant structural squiggles; publish is the authority.
    }
  }

  // The persistent line under the field carries errors only; warnings stay squiggle+hover.
  lintMessages.value = diagnostics.filter((d) => d.severity === "error").map((d) => d.message);
  emit("diagnostics", diagnostics);
  return diagnostics;
}

// Caret moves and focus changes shift the advisory typing-in-progress exemption, not just edits.
const lintNeedsRefresh = (update: ViewUpdate) => update.selectionSet || update.focusChanged;

// MUST be a stable object: vue-codemirror6's extension computed depends on it and dispatches a
// full reconfigure whenever its identity changes. An inline template literal here meant a
// reconfigure on EVERY keystroke — and a reconfigure landing mid-IME-composition (dead-key
// quotes on international keyboards) corrupts CodeMirror's DOM and crashes the editor.
const linterConfig = { delay: 300, needsRefresh: lintNeedsRefresh };

const singleLineFilter: Extension = EditorState.transactionFilter.of((tr) =>
  tr.newDoc.lines > 1 ? [] : tr,
);

const extensions = computed<Extension[]>(() => [
  // Highest precedence = lowest decoration rank = the slot marks nest INNERMOST, inside the base
  // language's token spans — so slot colours win by DOM inheritance where a slot sits mid-JSON-string
  // (CSS specificity can't reach across nested spans).
  Prec.highest(bbelSlotHighlight()),
  bbelBaseLangHighlight(),
  autocompletion({
    override: [bbelCompletionSource(() => props.scope), ...nativeSources.value],
    // Locked (secret) members render disabled — greyed with a lock, accept is a no-op.
    optionClass: (c) => ((c as { locked?: boolean }).locked ? "bbel-completion-locked" : ""),
  }),
  bbelFunctionHover(() => props.scope.functions),
  bbelSignatureHelp(() => props.scope.functions),
  // Parent tooltips (autocomplete + lint + hover) to <body> so they float above the service modal
  // instead of being clipped by its overflow — which otherwise hides the popup and forces scrollbars
  // on the modal.
  tooltips({ parent: document.body }),
  closeBrackets(),
  bracketMatching(),
  // Tab accepts the highlighted completion (the default keymap only binds Enter); when no completion
  // is open acceptCompletion declines, so Tab falls through to the browser's normal focus move.
  keymap.of([{ key: "Tab", run: acceptCompletion }, ...completionKeymap, ...closeBracketsKeymap]),
  caretPathTracker,
  ...(props.singleLine ? [singleLineFilter] : []),
]);

// --- Smart-picker insert target (#5683): the picker is a permanent column on the service panel
// (BBEL_PICKER_HOST_KEY); focusing this field makes it the picker's insert target until another
// field takes over. The picker never steals focus (its buttons mousedown.prevent), so the caret
// survives every insert; insert() re-focuses the editor regardless.
const cm = ref<{ view?: EditorView } | null>(null);
const wrapEl = ref<HTMLElement | null>(null);
const pickerHost = inject<BbelPickerHost | null>(BBEL_PICKER_HOST_KEY, null);

// One stable target object per field — registered at mount (cold-open auto-targeting) and
// re-announced on focus. Identity matters: the host clears `active` by reference on dispose.
let pickerTarget: BbelInsertTarget | null = null;
let disposePickerTarget: (() => void) | null = null;
onMounted(() => {
  if (props.disabled || !pickerHost || !wrapEl.value) return;
  pickerTarget = { insert: insertAtCaret, caretPaths, el: wrapEl.value };
  disposePickerTarget = pickerHost.register(pickerTarget);
});
onBeforeUnmount(() => disposePickerTarget?.());

// The resolved levels of the member chain at the caret (`{{ services.s1.output.items[0]. }}` →
// ["services", "services.s1", …, "services.s1.output.items[0]"]) — the picker mirrors them by
// expanding those branches, so typing and browsing stay in sync. Bracket hops and quoted keys
// resolve like dots (chainAncestorPaths).
const caretPaths = ref<string[]>([]);

const caretPathTracker: Extension = EditorView.updateListener.of((update) => {
  if (!update.selectionSet && !update.docChanged) return;
  const before = update.state.sliceDoc(0, update.state.selection.main.head);
  if (!insideSlot(before)) {
    caretPaths.value = [];
    return;
  }
  caretPaths.value = chainAncestorPaths(/[\w$.[\]"']*$/.exec(before)?.[0] ?? "");
});

function insertAtCaret(bare: string, cursorBackBy: number) {
  const view = cm.value?.view;
  if (!view) return;
  const { from, to } = view.state.selection.main;
  const inSlot = insideSlot(view.state.sliceDoc(0, from));
  const { text, backFromEnd } = planInsert(bare, cursorBackBy, inSlot);
  view.dispatch({
    changes: { from, to, insert: text },
    selection: { anchor: from + text.length - backFromEnd },
  });
  view.focus();
}

// Focus makes this field the picker's insert target (last-focused wins).
function onFieldFocusIn() {
  if (pickerHost && pickerTarget) pickerHost.setActive(pickerTarget);
}

defineExpose({
  /** Focus the editor (the fullscreen modal focuses its field on open). */
  focus: () => cm.value?.view?.focus(),
});
</script>

<template>
  <div ref="wrapEl" class="bbel-field-wrap" @focusin="onFieldFocusIn">
    <CodeMirror
      ref="cm"
      v-model="value"
      :lang="baseLang"
      :linter="lintSource"
      :linter-config="linterConfig"
      :extensions="extensions"
      :placeholder="placeholder"
      :disabled="disabled"
      :wrap="true"
      :minimal="true"
      :class="['bbel-field', { 'bbel-field--single': singleLine, 'bbel-field--body': fieldShape === 'body' }]"
    />
    <!-- Corner expand → fullscreen editor (n8n pattern): visible on hover/focus only, mousedown.prevent
         so a click doesn't disturb the caret before the modal takes over. -->
    <button
      v-if="expandable && !disabled"
      type="button"
      class="bbel-expand"
      title="Open fullscreen editor"
      aria-label="Open fullscreen editor"
      @mousedown.prevent
      @click="expanded = true"
    >
      <i class="bi bi-arrows-angle-expand"></i>
    </button>
    <div v-if="lintMessages.length" class="bbel-field-error small text-danger" :title="lintMessages.join('\n')">
      <i class="bi bi-exclamation-circle me-1"></i>{{ lintMessages[0]
      }}<span v-if="lintMessages.length > 1" class="text-secondary"> +{{ lintMessages.length - 1 }} more</span>
    </div>
    <!-- Live-sync: the modal edits this same model through the same emit chain — no save/cancel. -->
    <BbelEditorModal
      v-if="expanded"
      v-model="value"
      :scope="scope"
      :field-shape="fieldShape"
      :lang="lang"
      :placeholder="placeholder"
      :single-line="singleLine"
      @close="expanded = false"
    />
  </div>
</template>

<style scoped>

.bbel-field-wrap {
  position: relative;
}
/* Stock .form-control metrics (1.5 line-height / .375rem .75rem padding), with the content at
   Bootstrap's monospace convention ($code-font-size: .875em — code/pre/kbd all render at 87.5% of
   surrounding text, because mono faces look oversized at nominal size). */
.bbel-field {
  border: 1px solid var(--bs-border-color, #dee2e6);
  border-radius: var(--bs-border-radius);
  font-family: var(--bs-font-monospace, monospace);
  font-size: 0.875em;
  line-height: 1.5;
  background: var(--bs-body-bg, #fff);
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}
/* Focus treatment lives in scss/styles.scss (.bbel-field:focus-within) — it needs the theme's
   SCSS input-focus tokens to match .form-control:focus exactly. */
.bbel-field-error {
  margin-top: 0.15rem;
  line-height: 1.3;
}
.bbel-expand {
  position: absolute;
  top: 0.2rem;
  right: 0.2rem;
  z-index: 2;
  padding: 0 0.3rem;
  font-size: 0.7rem;
  line-height: 1.4;
  color: var(--bs-secondary-color);
  background: none;
  border: 0;
  opacity: 0;
  transition: opacity 0.15s ease-in-out;
}
.bbel-field-wrap:hover .bbel-expand,
.bbel-field-wrap:focus-within .bbel-expand {
  opacity: 1;
}
.bbel-expand:hover {
  color: var(--bs-primary);
}
.bbel-field :deep(.cm-editor) {
  border-radius: var(--bs-border-radius);
}
/* CM's base theme puts 1.4 on the scroller — restate the form-control line-height there. */
.bbel-field :deep(.cm-scroller) {
  line-height: 1.5;
}
.bbel-field :deep(.cm-editor.cm-focused) {
  outline: none;
}
.bbel-field :deep(.cm-content) {
  padding: 0.375rem 0.75rem;
}
/* Single-line fields soft-wrap as the value grows (real newlines stay blocked by the transaction
   filter), capped at four visual lines before the editor scrolls vertically. */
.bbel-field--single :deep(.cm-scroller) {
  max-height: calc(6em + 0.75rem);
}
/* Body-shaped fields open at textarea height instead of growing from one line. */
.bbel-field--body :deep(.cm-content) {
  min-height: 8em;
}

</style>

<style>
/* Tooltips (autocomplete + lint) are parented to <body> (see the tooltips extension), so this must be
   global — the tooltip DOM lives outside this component's scoped tree. CodeMirror's base theme sets a
   low z-index on .cm-tooltip and injects it after this rule (same specificity), so !important is needed
   to win; the value clears the service modal (the legacy smart-picker sat at 1080). */
.cm-tooltip {
  z-index: 10000 !important;
  /* CodeMirror colours its widgets from its own light/dark theme flag (default: light), not from
     CSS — in dark mode every tooltip came out #f5f5f5. Restyle on Bootstrap's colour-mode variables
     so lint hovers, function hovers, signature help, and autocomplete all follow data-bs-theme. */
  background-color: var(--bs-body-bg) !important;
  color: var(--bs-body-color) !important;
  border: 1px solid var(--bs-border-color) !important;
  border-radius: var(--bs-border-radius);
  box-shadow: var(--bs-box-shadow-sm, 0 1px 3px rgba(0, 0, 0, 0.15));
}
.cm-tooltip.cm-tooltip-autocomplete > ul > li[aria-selected] {
  background-color: var(--bs-primary) !important;
  color: #fff !important;
}
/* Locked (secret) members: greyed with a trailing lock, matching the picker tree's treatment. */
.cm-tooltip.cm-tooltip-autocomplete > ul > li.bbel-completion-locked {
  color: var(--bs-secondary-color);
}
.cm-tooltip.cm-tooltip-autocomplete > ul > li.bbel-completion-locked .cm-completionLabel::after {
  font-family: bootstrap-icons;
  content: "\F47A"; /* bi-lock */
  margin-left: 0.35rem;
  font-size: 0.75em;
}
.cm-tooltip .cm-completionInfo {
  background-color: var(--bs-body-bg) !important;
  color: var(--bs-body-color) !important;
  border-color: var(--bs-border-color) !important;
}
.bbel-fn-hover {
  max-width: 22rem;
  padding: 0.4rem 0.55rem;
}
.bbel-fn-hover-sig {
  display: block;
  font-family: var(--bs-font-monospace, monospace);
  font-size: 0.78rem;
  color: var(--bs-emphasis-color, #212529);
}
.bbel-fn-hover-sum {
  margin-top: 0.2rem;
  font-size: 0.78rem;
  color: var(--bs-secondary-color, #6c757d);
}
.bbel-sig {
  max-width: 24rem;
  padding: 0.4rem 0.55rem;
}
.bbel-sig-code {
  display: block;
  font-family: var(--bs-font-monospace, monospace);
  font-size: 0.78rem;
  color: var(--bs-secondary-color, #6c757d);
}
.bbel-sig-active {
  color: var(--bs-emphasis-color, #212529);
  font-weight: 600;
}
.bbel-sig-sum {
  margin-top: 0.2rem;
  font-size: 0.78rem;
  color: var(--bs-secondary-color, #6c757d);
}

/* Base-language token palette (bbel-base-* classes from bbel-language.ts) — the JSON/XML colouring
   under the slots. Single-class selectors on purpose: the slot rules below carry the .bbel-field
   prefix (higher specificity), so wherever a slot sits inside a base token — e.g. mid-JSON-string —
   the BBEL colours win. */
.bbel-base-key {
  color: var(--bs-info-text-emphasis, #055160);
}
.bbel-base-string {
  color: var(--bs-danger-text-emphasis, #58151c);
}
.bbel-base-number {
  color: var(--bs-success-text-emphasis, #0a3622);
}
.bbel-base-atom {
  color: var(--bs-warning-text-emphasis, #664d03);
}
.bbel-base-comment {
  color: var(--bs-secondary-color, #6c757d);
}

/* CodeMirror's base theme draws the caret and selection for its default light mode (black caret,
   pale-blue selection) regardless of data-bs-theme; !important for the same injection-order reason
   as .cm-tooltip above. */
.bbel-field .cm-content {
  caret-color: var(--bs-body-color) !important;
}
.bbel-field .cm-cursor {
  border-left-color: var(--bs-body-color) !important;
}
/* Native inputs get the browser's vivid ::selection; CodeMirror paints its own layer, so match it —
   translucent primary reads on both themes and sits close to the OS default other fields show. */
.bbel-field .cm-selectionBackground {
  background-color: rgba(13, 110, 253, 0.25) !important;
}
.bbel-field .cm-focused .cm-selectionBackground,
.bbel-field.cm-focused .cm-selectionBackground {
  background-color: rgba(13, 110, 253, 0.4) !important;
}
</style>
